import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Chip,
} from "@mui/material";
import { ArrowUpward, ArrowDownward, Delete, Add } from "@mui/icons-material";
import { searchProducts, ProductLite } from "../../api/productSearchApi";
import type { HeroProductInput, HeroProductRow } from "../../api/adminHeroApi";

type Props = {
  value: HeroProductInput[];
  onChange: (next: HeroProductInput[]) => void;
  resolved?: HeroProductRow[]; // optional server-resolved products
};

export default function FeaturedProductsPicker({ value, onChange, resolved }: Props) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<ProductLite[]>([]);
  const [loading, setLoading] = useState(false);

  // cache products we’ve seen via search so selected can show name/image immediately
  const [cache, setCache] = useState<Map<number, ProductLite>>(new Map());

  const selectedIds = useMemo(() => new Set(value.map((v) => v.product_id)), [value]);

  const resolvedMap = useMemo(() => {
    const m = new Map<number, HeroProductRow["product"]>();
    (resolved || []).forEach((r) => m.set(r.product_id, r.product));
    return m;
  }, [resolved]);

  const debounceRef = useRef<number | null>(null);

  // debounced search while typing
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setResults([]);
      return;
    }

    if (debounceRef.current) window.clearTimeout(debounceRef.current);

    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        const data = await searchProducts(term);
        const arr = Array.isArray(data) ? data : [];
        setResults(arr);

        setCache((prev) => {
          const next = new Map(prev);
          arr.forEach((p) => next.set(p.id, p));
          return next;
        });
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q]);

  async function runSearch() {
    const term = q.trim();
    if (!term) return;
    setLoading(true);
    try {
      const data = await searchProducts(term);
      const arr = Array.isArray(data) ? data : [];
      setResults(arr);

      setCache((prev) => {
        const next = new Map(prev);
        arr.forEach((p) => next.set(p.id, p));
        return next;
      });
    } finally {
      setLoading(false);
    }
  }

  function addProduct(p: ProductLite) {
    if (selectedIds.has(p.id)) return;

    // ensure cache has it so Selected can show immediately
    setCache((prev) => {
      const next = new Map(prev);
      next.set(p.id, p);
      return next;
    });

    const next: HeroProductInput[] = [
      ...value,
      { product_id: p.id, sort_order: value.length, badge_text: null, use_product_image: true },
    ];
    onChange(next);
  }

  function removeProduct(productId: number) {
    const next = value
      .filter((v) => v.product_id !== productId)
      .map((v, idx) => ({ ...v, sort_order: idx }));
    onChange(next);
  }

  function move(productId: number, dir: -1 | 1) {
    const idx = value.findIndex((v) => v.product_id === productId);
    if (idx < 0) return;
    const nextIdx = idx + dir;
    if (nextIdx < 0 || nextIdx >= value.length) return;

    const next = [...value];
    [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
    onChange(next.map((v, i) => ({ ...v, sort_order: i })));
  }

  function setBadge(productId: number, badge: string) {
    const next = value.map((v) =>
      v.product_id === productId ? { ...v, badge_text: badge.trim() ? badge : null } : v
    );
    onChange(next);
  }

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Featured Products
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center">
          <TextField
            fullWidth
            label="Search products"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") runSearch();
            }}
          />
          <Button variant="contained" onClick={runSearch} disabled={loading || !q.trim()}>
            Search
          </Button>
        </Stack>

        {!!results.length && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Results
            </Typography>
            <Stack spacing={1}>
              {results.slice(0, 8).map((p) => (
                <Stack
                  key={p.id}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
                >
                  <Avatar variant="rounded" src={p.image_url || undefined}>
                    {p.name?.[0] || "P"}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={600}>{p.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      #{p.id} • {p.min_variant_price != null ? `From $${p.price}` : `$${p.price}`}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    variant={selectedIds.has(p.id) ? "outlined" : "contained"}
                    disabled={selectedIds.has(p.id)}
                    onClick={() => addProduct(p)}
                  >
                    {selectedIds.has(p.id) ? "Added" : "Add"}
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Selected
        </Typography>

        {!value.length ? (
          <Typography variant="body2" color="text.secondary">
            No featured products yet.
          </Typography>
        ) : (
          <List dense>
            {value
              .slice()
              .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
              .map((v, i) => {
                const prod = resolvedMap.get(v.product_id) || cache.get(v.product_id);

                return (
                  <ListItem
                    key={v.product_id}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, mb: 1 }}
                    secondaryAction={
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <IconButton onClick={() => move(v.product_id, -1)} disabled={i === 0}>
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => move(v.product_id, 1)} disabled={i === value.length - 1}>
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                        <IconButton onClick={() => removeProduct(v.product_id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Stack>
                    }
                  >
                    <ListItemAvatar>
                      <Avatar variant="rounded" src={prod?.image_url || undefined}>
                        {prod?.name?.[0] || "P"}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Typography fontWeight={700}>{prod?.name || `Product #${v.product_id}`}</Typography>
                          <Chip size="small" label={`Order ${i + 1}`} />
                          {/* Only show badge if badge_text exists and is not empty */}
                          {v.badge_text && v.badge_text.trim() !== "" ? (
                            <Chip 
                              size="small" 
                              color="primary" 
                              label={v.badge_text} 
                              sx={{ 
                                backgroundColor: "rgba(0, 200, 255, 0.15)",
                                color: "#00ccff",
                                border: "1px solid rgba(0, 200, 255, 0.3)",
                              }}
                            />
                          ) : null}
                        </Stack>
                      }
                      secondary={
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="center" mt={1}>
                          <TextField
                            size="small"
                            label="Badge (optional)"
                            value={v.badge_text || ""}
                            onChange={(e) => setBadge(v.product_id, e.target.value)}
                            sx={{ width: { xs: "100%", sm: 260 } }}
                            placeholder="e.g. New, Limited, Sale"
                          />
                          <Typography variant="body2" color="text.secondary">
                            product_id: {v.product_id}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              })}
          </List>
        )}
      </CardContent>
    </Card>
  );
}